import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, MonitorUp, Plus, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Badge } from '@/shared/ui/components/badge';
import {
  useCreatePosDevice,
  usePosDevices,
  useRevokePosDevice,
  type PosInstallation,
} from './posDevices';

export function PosDevicesPage() {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const { venueId } = useVenueContext();
  const scope = { kind: 'venue', venueId } as const;
  const canManage = auth.can('pos.installation.enroll', scope);
  const devices = usePosDevices(venueId, canManage);
  const createDevice = useCreatePosDevice(venueId);
  const revokeDevice = useRevokePosDevice(venueId);
  const [name, setName] = useState('');
  const [createdCode, setCreatedCode] = useState<{ name: string; code: string } | null>(null);

  if (!canManage) return <Forbidden />;

  async function create() {
    const trimmed = name.trim();
    if (!trimmed) return;
    const created = await createDevice.mutateAsync(trimmed);
    setCreatedCode({ name: created.device.name, code: created.enrollmentCode });
    setName('');
  }

  async function revoke(device: PosInstallation) {
    await revokeDevice.mutateAsync(device.id);
    toast.success(t('posDevices.toast.revoked', { name: device.name }));
  }

  async function copyCode() {
    if (!createdCode) return;
    await navigator.clipboard?.writeText(createdCode.code);
    toast.success(t('posDevices.toast.copied'));
  }

  const items = devices.data?.items ?? [];

  return (
    <section className="grid max-w-4xl gap-5 p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid gap-1">
          <h1 className="text-2xl font-semibold">{t('posDevices.title')}</h1>
          <p className="text-muted-foreground">{t('posDevices.description')}</p>
        </div>
      </header>

      <form
        className="grid gap-3 rounded-lg border bg-card p-4 md:grid-cols-[1fr_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          void create();
        }}
      >
        <Input
          aria-label={t('posDevices.nameLabel')}
          placeholder={t('posDevices.namePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Button type="submit" disabled={!name.trim() || createDevice.isPending}>
          <Plus className="size-4" /> {t('posDevices.create')}
        </Button>
      </form>

      {createdCode && (
        <div className="grid gap-3 rounded-lg border border-primary/40 bg-accent/30 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="grid gap-1">
              <h2 className="text-base font-semibold">
                {t('posDevices.enrollmentCodeTitle', { name: createdCode.name })}
              </h2>
              <p className="text-sm text-muted-foreground">{t('posDevices.enrollmentCodeHint')}</p>
            </div>
            <Button type="button" variant="outline" onClick={copyCode}>
              <Copy className="size-4" /> {t('posDevices.copyCode')}
            </Button>
          </div>
          <code className="w-fit rounded-md bg-background px-3 py-2 font-mono text-2xl font-semibold tracking-normal">
            {createdCode.code}
          </code>
        </div>
      )}

      <div className="grid gap-2">
        {devices.isLoading && <p className="text-muted-foreground">{t('posDevices.loading')}</p>}
        {!devices.isLoading && items.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('posDevices.empty')}</p>
        )}
        {items.map((device) => (
          <div
            key={device.id}
            className="grid gap-3 rounded-lg border p-4 md:grid-cols-[1fr_auto] md:items-center"
          >
            <div className="flex min-w-0 items-start gap-3">
              <MonitorUp className="mt-0.5 size-5 shrink-0 text-primary" />
              <div className="grid min-w-0 gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{device.name}</span>
                  <Badge variant={device.state === 'ACTIVE' ? 'default' : 'secondary'}>
                    {t(`posDevices.state.${device.state}`)}
                  </Badge>
                </div>
                <span className="text-sm text-muted-foreground">{statusDetail(device, t)}</span>
              </div>
            </div>
            {device.state !== 'REVOKED' && (
              <Button
                type="button"
                variant="outline"
                disabled={revokeDevice.isPending}
                onClick={() => void revoke(device)}
              >
                <ShieldOff className="size-4" /> {t('posDevices.revoke')}
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function statusDetail(device: PosInstallation, t: ReturnType<typeof useTranslation>['t']): string {
  if (device.state === 'PENDING' && device.enrollmentCodeExpiresAt) {
    return t('posDevices.pendingUntil', {
      time: new Date(device.enrollmentCodeExpiresAt).toLocaleTimeString(),
    });
  }
  if (device.state === 'ACTIVE' && device.enrolledAt) {
    return t('posDevices.enrolledAt', { date: new Date(device.enrolledAt).toLocaleString() });
  }
  if (device.state === 'REVOKED' && device.revokedAt) {
    return t('posDevices.revokedAt', { date: new Date(device.revokedAt).toLocaleString() });
  }
  return t('posDevices.noTimestamp');
}
